import React, { useState } from 'react';
import {
  Instagram,
  Megaphone,
  Lightbulb,
  Trophy,
  Calendar,
  MessageCircle,
  Heart,
  Share2,
  MessageSquare,
  ExternalLink,
  Users
} from 'lucide-react';
import { defaultConfig } from '../../config/academyConfig';

interface CommunityPost {
  id: string;
  type: 'instagram' | 'announcement' | 'tip' | 'success-story' | 'event';
  title: string;
  content: string;
  author?: string;
  authorRole?: string;
  imageUrl?: string;
  link?: string;
  likes: number;
  comments: number;
  timestamp: string;
}

// Demo posts - in production these come from the API
const DEMO_POSTS: CommunityPost[] = [
  {
    id: '1',
    type: 'success-story',
    title: 'From Side Hustle to Full-Time',
    content: 'Meet Sarah from Coastal Coffee Co. She started with YourBeans 18 months ago and just quit her day job to run her coffee business full-time!',
    author: 'Sarah Mitchell',
    authorRole: 'Coastal Coffee Co.',
    likes: 47,
    comments: 12,
    timestamp: '2 days ago'
  },
  {
    id: '2',
    type: 'tip',
    title: 'Marketing Tip: User-Generated Content',
    content: 'Encourage customers to share photos of your coffee on Instagram. Repost their content and watch your engagement soar. UGC builds trust and community!',
    author: 'YourBeans Team',
    authorRole: 'Marketing',
    likes: 32,
    comments: 8,
    timestamp: '3 days ago'
  },
  {
    id: '3',
    type: 'announcement',
    title: 'New Blend Available: Ethiopian Yirgacheffe',
    content: 'We\'ve added a stunning single-origin Ethiopian Yirgacheffe to our range. Bright, fruity, with notes of blueberry and citrus. Perfect for summer!',
    author: 'YourBeans',
    likes: 56,
    comments: 15,
    timestamp: '5 days ago'
  },
  {
    id: '4',
    type: 'event',
    title: 'Live Q&A: Building Your First 100 Customers',
    content: 'Join us this Thursday for a deep-dive session on customer acquisition strategies that actually work for coffee brands.',
    author: 'YourBeans Academy',
    likes: 28,
    comments: 6,
    timestamp: '1 week ago'
  }
];

const TYPE_ICONS = {
  instagram: Instagram,
  announcement: Megaphone,
  tip: Lightbulb,
  'success-story': Trophy,
  event: Calendar
};

const CommunityFeed: React.FC = () => {
  const [posts] = useState<CommunityPost[]>(DEMO_POSTS);

  const getTypeBadge = (type: CommunityPost['type']) => {
    switch (type) {
      case 'instagram': return { bg: 'bg-pink-100', text: 'text-pink-600', label: 'Instagram' };
      case 'announcement': return { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Announcement' };
      case 'tip': return { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Tip' };
      case 'success-story': return { bg: 'bg-green-100', text: 'text-green-600', label: 'Success Story' };
      case 'event': return { bg: 'bg-purple-100', text: 'text-purple-600', label: 'Event' };
      default: return { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Post' };
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Community</h2>
        <a
          href={defaultConfig.socialLinks.instagram}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-amber-600 font-medium flex items-center hover:underline"
        >
          <Instagram className="w-4 h-4 mr-1" />
          Follow Us
        </a>
      </div>

      {/* Social Links Banner */}
      <div className="grid grid-cols-2 gap-3">
        <a
          href={defaultConfig.socialLinks.instagram}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center p-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:opacity-90 transition-opacity"
        >
          <Instagram className="w-5 h-5 mr-2" />
          <span className="font-medium">Instagram</span>
        </a>
        <a
          href={defaultConfig.socialLinks.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center p-3 bg-blue-600 text-white rounded-xl hover:opacity-90 transition-opacity"
        >
          <Users className="w-5 h-5 mr-2" />
          <span className="font-medium">FB Group</span>
        </a>
      </div>

      {/* Posts Feed */}
      <div className="space-y-4">
        {posts.map(post => {
          const badge = getTypeBadge(post.type);
          const TypeIcon = TYPE_ICONS[post.type] || MessageCircle;

          return (
            <div key={post.id} className="bg-white rounded-xl p-4 shadow-sm">
              {/* Post Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center mr-3">
                    <TypeIcon className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{post.author}</div>
                    {post.authorRole && (
                      <div className="text-xs text-gray-500">{post.authorRole}</div>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
                  {badge.label}
                </span>
              </div>

              {/* Post Content */}
              <h3 className="font-semibold text-gray-900 mb-2">{post.title}</h3>
              <p className="text-gray-600 text-sm">{post.content}</p>

              {/* Post Image (if any) */}
              {post.imageUrl && (
                <div className="mt-3 rounded-lg overflow-hidden">
                  <img src={post.imageUrl} alt="" className="w-full h-48 object-cover" />
                </div>
              )}

              {/* Post Footer */}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  <button className="flex items-center hover:text-amber-600 transition-colors">
                    <Heart className="w-4 h-4 mr-1" />
                    {post.likes}
                  </button>
                  <button className="flex items-center hover:text-amber-600 transition-colors">
                    <MessageCircle className="w-4 h-4 mr-1" />
                    {post.comments}
                  </button>
                  <button className="flex items-center hover:text-amber-600 transition-colors">
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
                <span className="text-xs text-gray-400">{post.timestamp}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Join Community CTA */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-center">
          <MessageSquare className="w-8 h-8 text-amber-500 mr-4" />
          <div className="flex-1">
            <h4 className="font-semibold text-gray-900">Join the Conversation</h4>
            <p className="text-sm text-gray-600">
              Connect with 800+ coffee entrepreneurs in our Facebook group
            </p>
          </div>
          <a
            href={defaultConfig.socialLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-amber-500 text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-amber-600 transition-colors flex items-center"
          >
            <ExternalLink className="w-4 h-4 mr-1" />
            Join
          </a>
        </div>
      </div>
    </div>
  );
};

export default CommunityFeed;
